"use client";
import React, { useState, useRef, useCallback, useEffect, useMemo, CSSProperties } from "react";
import cn from "classnames";
import s from "./AccordionItem.module.scss";
import { SpriteIcon } from "components/UI/SpriteIcon";
import ArrowDownIcon from "/public/img/icons/arrow-down.svg";
import { upperFirst } from "lodash";
import { adjustArticleContent } from "utils/helpers";

export type AccordionItemProps = {
  question?: string;
  answer?: string;
  type?: "primary" | "secondary" | "numbered";
  questionBorder?: boolean;
  initiallyOpen?: boolean;
  className?: string;
  microData?: boolean;
  questionComponent?: React.ElementType;
  answerComponent?: React.ElementType;
  orderNumber?: number;
};

const questionMicroDataSchema = {
  itemScope: true,
  itemProp: "mainEntity",
  itemType: "https://schema.org/Question",
};

const answerMicroDataSchema = {
  itemScope: true,
  itemProp: "acceptedAnswer",
  itemType: "https://schema.org/Answer",
};

export const AccordionItem = ({
  question,
  answer,
  type = "primary",
  initiallyOpen = false,
  className,
  microData = true,
  questionComponent: QuestionComponent,
  answerComponent: AnswerComponent,
  orderNumber,
}: AccordionItemProps) => {
  const [active, setActive] = useState(false);

  const textWrap = useRef<HTMLDivElement>(null);
  const answerContainer = useRef<HTMLDivElement>(null);

  const checkTextWrap = useCallback(() => {
    const node = textWrap.current;
    if (!node) return;
    if (active) {
      node.style.maxHeight = node.scrollHeight + "px";
    } else {
      node.style.maxHeight = "";
    }
  }, [active]);

  useEffect(() => {
    if (initiallyOpen) setActive(true);
  }, [initiallyOpen]);

  useEffect(() => {
    window.addEventListener("resize", checkTextWrap);

    return () => {
      window.removeEventListener("resize", checkTextWrap);
    };
  }, [checkTextWrap]);

  useEffect(() => {
    checkTextWrap();
  }, [active, checkTextWrap]);

  // adjust content
  useEffect(() => {
    if (!answerContainer.current) return;

    adjustArticleContent(answerContainer.current);
  }, [answerContainer]);

  const questionMicroData = useMemo(() => (microData ? questionMicroDataSchema : {}), [microData]);
  const answerMicroData = useMemo(() => (microData ? answerMicroDataSchema : {}), [microData]);
  const questionItemProp = useMemo(() => (microData ? { itemProp: "name" } : {}), [microData]);
  const answerItemProp = useMemo(() => (microData ? { itemProp: "text" } : {}), [microData]);

  return (
    <div
      className={cn(
        s.accordionItem,
        s[`accordionItemType${upperFirst(type)}`],
        {
          [s.accordionItemActive]: active,
          [s.withNumber]: !!orderNumber,
        },
        className
      )}
      {...questionMicroData}
    >
      {/*  extra class required for question height adjustment */}
      <div
        className={cn("sb-accordion-item-question", s.accordionItemQuestion)}
        onClick={() => setActive(!active)}
      >
        {QuestionComponent ? (
          <QuestionComponent />
        ) : (
          <div
            className={s.accordionItemQuestionText}
            dangerouslySetInnerHTML={{ __html: question || "" }}
            data-order-number={orderNumber}
            {...questionItemProp}
          />
        )}

        <span className={s.accordionItemBtn}>
          <SpriteIcon icon={ArrowDownIcon} />
        </span>
      </div>
      <div className={s.accordionItemAnswer} {...answerMicroData} ref={textWrap}>
        {AnswerComponent ? (
          <div className={s.accordionItemAnswerTextInner}>
            <AnswerComponent />
          </div>
        ) : (
          <div
            className={s.accordionItemAnswerTextInner}
            dangerouslySetInnerHTML={{ __html: answer || "" }}
            ref={answerContainer}
            {...answerItemProp}
          />
        )}
      </div>
    </div>
  );
};
